/* eslint-disable react/prop-types */
/* eslint-disable import/no-extraneous-dependencies */

// Polyfill support for IE and legacy browsers
import 'custom-event-polyfill';
import 'core-js/es6';
import 'regenerator-runtime/runtime';
import 'react-app-polyfill/ie9';
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import VZThemeProvider from '@vz-soe-utils/vzthemeprovider';
import './ImportComponents';

import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';

import { ModelManager, Constants } from '@adobe/cq-spa-page-model-manager';

// Typography / icons
import './assets/fonts/verizon-fonts/fonts.css';
import './assets/fonts/verizon-icons/fonts.css';
import './assets/css/layout.scss';

// Tooltip
import 'rc-tooltip/assets/bootstrap.css';

// PrimeReact, PrimeIcons, and VZPrime theme
import '@vzprime/react/resources/utils/util-pixelSpacingAndText.css';
import '@vzprime/react/resources/themes/verizon-react.css';
import './assets/css/vzprime-extend.css';

import { VDSManager } from '@vds/utilities';
import App from './components/App';
import { store } from './configureStore';
import history from './utils/history';
import ScrollToTop from './utils/RouteHelper';
import VoiceBot from './components/NextGen/VoiceBot';
import { VoiceBotProvider } from './components/NextGen/VoiceBot/contexts/VoiceBotContext';

import PageHeader from './components/common/PageHeader';
import PageFooter from './components/common/PageFooterRedesign';
import FooterPlaceholder from './components/common/FooterPlaceholder';

import { getModelJsonPath } from './ModelUtils';
import {
  getCookie,
  getUrlName,
  isExpressCheckout,
  isExpressCheckoutNSE,
  isMVA,
  loadVzGptChatbotScript,
  scrollToTop,
} from './components/common/Helpers';
import { handlePrivacyBannerOnRouting } from './components/common/Helpers/privacyBanner';
import GlobalRibbon from './components/common/GlobalRibbon';
import { mvaGlobalNavUpdate } from './components/common/Helpers/app';

const MOUNT_NODE = document.getElementById('page');
console.log('bootstrap executed');

function render(pageModel) {
  console.debug('inside render');
  // eslint-disable-next-line react/no-deprecated
  ReactDOM.render(RootApp(pageModel), MOUNT_NODE);
}

const InitApp = ({ pageModel }) => {
  console.debug('inside render');
  const location = useLocation();
  const pageName = getUrlName(window.location.pathname);
  const isSMLPOrdering = window.location.pathname?.includes('sales/home/streamlineordering');

  useEffect(() => {
    const headerElem = document.body;
    const isLoadVzGptScript = getCookie('load_vz_gpt_script') === 'true';
    const isVzChatEnabled = getCookie('vzChatEnabled') === 'true';
    if (isLoadVzGptScript || isVzChatEnabled) {
      loadVzGptChatbotScript({ forceLoad: isVzChatEnabled });
    }
    if (headerElem) {
      headerElem.focus();
    }
  }, []);

  useEffect(() => {
    scrollToTop();
    handlePrivacyBannerOnRouting();
    if (isMVA()) {
      mvaGlobalNavUpdate(getUrlName(location?.pathname));
    }
  }, [location]);
  const vdsProperties = {
    minDesktop: '1232px',
    maxTablet: '1231px',
    maxTabletLarge: '768px',
  };
  const vdsAddons = isSMLPOrdering ? vdsProperties : {};
  const voiceBotFeatureFlags = {
    cesVoiceBotFFlag: pageModel.cesVoiceBotFFlag || { enabled: false },
  };
  const isVoiceBotFeatureEnabled = getCookie('cesVoiceBot') === 'true' || getCookie('newVoiceBot') === 'true';

  return (
    <VZThemeProvider>
      <>
        <VDSManager {...vdsAddons} />
        {isExpressCheckoutNSE() && <GlobalRibbon props={pageModel} pageName={pageName} />}
        <PageHeader
          history={history}
          taxFeeText={pageModel.taxFeeText}
          disneyPromoHelperText={pageModel.disneyPromoHelperText}
          acpBannerContent={pageModel.acpBannerContent}
          acpBannerSpanishContent={pageModel.acpBannerSpanishContent}
          acpBannerFlag={pageModel.acpBannerFlag}
          isSigninLink={pageModel.isSigninLink}
          isSigninPrompt={pageModel.isSigninPrompt}
          withAutoPayPreview={pageModel.withAutoPayPreview}
          withAutoPayTooltipDesc={pageModel.withAutoPayTooltipDesc}
        />
        {!isExpressCheckoutNSE() && <GlobalRibbon props={pageModel} pageName={pageName} />}
        <App
          history={history}
          cqChildren={pageModel[Constants.CHILDREN_PROP]}
          cqItems={pageModel[Constants.ITEMS_PROP]}
          cqItemsOrder={pageModel[Constants.ITEMS_ORDER_PROP]}
          cqPath={pageModel[Constants.PATH_PROP]}
          locationPathname={window.location.pathname}
          apiServer={pageModel.apiServer}
          isProd={pageModel.isProd}
          colorTheme={pageModel.colorTheme}
          whwPlusName={pageModel.whwPlusName}
          whwBasicName={pageModel.whwBasicName}
          extendertext={pageModel.extendertext}
          acpTagging={pageModel.acpTagging}
          isWHWISPU={pageModel.isWHWISPU}
          isWHWPhase2={pageModel.isWHWPhase2}
          whwUpgradeOnly={pageModel.whwUpgradeOnly}
          whwBasicQuantity={pageModel.whwBasicQuantity}
          whwPlusQuantity={pageModel.whwPlusQuantity}
          isWHWPhase2Term={pageModel.isWHWPhase2Term}
          isWHWPCartRestart={pageModel.isWHWPCartRestart}
          localOfferFlag={pageModel.localOfferFlag}
          excludeIndirect={pageModel.excludeIndirect}
          issetupEdit={pageModel.issetupEdit}
          enableVideoCall={pageModel.enableVideoCall}
          disableChatLinks={pageModel.disableChatLinks}
          isInvalidUnitNum={pageModel.isInvalidUnitNum}
          integratedCartContactInfo={pageModel.integratedCartContactInfo}
          iscontactButtonEnabled={pageModel.iscontactButtonEnabled}
          getCheckOutDetails={pageModel.getCheckOutDetails}
          shippingGetCheckoutNSE={pageModel.shippingGetCheckoutNSE}
          showVZVisaCard={pageModel.showVZVisaCard}
          isSkipMtnApi={pageModel.isSkipMtnApi}
          sessionTimeoutTime={pageModel.sessionTimeoutTime}
          sessionTimeoutWarningTime={pageModel.sessionTimeoutWarningTime}
          registrationTimeoutNonProd={pageModel.registrationTimeoutNonProd}
          registrationTimeoutProd={pageModel.registrationTimeoutProd}
          asyncRequestWatcher={pageModel.asyncRequestWatcher}
          pdcFlag={pageModel.pdcFlag}
          fwaModalFlag={pageModel.fwaModalFlag}
          modalFwaTitle={pageModel.modalFwaTitle}
          fwaModalDesktopTimeout={pageModel.fwaModalDesktopTimeout}
          fwaModalMobileTimeout={pageModel.fwaModalMobileTimeout}
          fwaModalMobileImgUrl={pageModel.fwaModalMobileImgUrl}
          fwaModalDesktopImgUrl={pageModel.fwaModalDesktopImgUrl}
          invalidCaseStatusCode={pageModel.invalidCaseStatusCode}
          msgwithLinkErrorCode={pageModel.msgwithLinkErrorCode}
          isValidateCartCall={pageModel.isValidateCartCall}
          miniCartApi={pageModel.miniCartApi}
          localStorage={pageModel.localStorage}
          isLoader={pageModel.isLoader}
          enableAPFuncComp={pageModel.enableAPFuncComp}
          acpIntegrated={pageModel.acpIntegrated}
          vfIntegrated={pageModel.vfIntegrated}
          vfNyIntegrated={pageModel.vfNyIntegrated}
          plansVisitLq={pageModel.plansVisitLq}
          isBroadBandFacts={pageModel.isBroadBandFacts}
          nonVzConsent_dark={pageModel.nonVzConsent_dark}
          lteHomePlus={pageModel.lteHomePlus}
          lteHome={pageModel.lteHome}
          fiveGHomePlus={pageModel.fiveGHomePlus}
          fiveGHome={pageModel.fiveGHome}
          isUnitTypeAhead={pageModel.isUnitTypeAhead}
          isOnlyTypeAhead={pageModel.isOnlyTypeAhead}
          useTypeAheadSoeUrl={pageModel.useTypeAheadSoeUrl}
          enablePegaProcessStep={pageModel.enablePegaProcessStep}
          expressCheckoutPegaStep={pageModel.expressCheckoutPegaStep}
          enablePlanInfoAPI={pageModel.enablePlanInfoAPI}
          removeLQResponse={pageModel.removeLQResponse}
          isEPC={pageModel.isEPC}
          isSPARedirectionEnabled={pageModel.isSPARedirectionEnabled}
          isDiscountEnabled={pageModel.isDiscountEnabled}
          isSalesApproach={pageModel.isSalesApproach}
          isYoutubePremiumEnabled={pageModel.isYoutubePremiumEnabled}
          enableCookieKeys={pageModel.enableCookieKeys}
          prefetchAddons={pageModel.prefetchAddons}
          fetchAddOns={pageModel.fetchAddOns}
          prefetchGetShipping={pageModel.prefetchGetShipping}
          addPlanOnPlanSelection={pageModel.addPlanOnPlanSelection}
          isCartPromos={pageModel.isCartPromos}
          isSigninLink={pageModel.isSigninLink}
          isSigninPrompt={pageModel.isSigninPrompt}
          enableDisneyBundlePromo={pageModel.enableDisneyBundlePromo}
          redirectToPerks={pageModel.redirectToPerks}
          disableLocalStoargeUpdate={pageModel.disableLocalStoargeUpdate}
          isDigital={pageModel.isDigital}
          isMfeNbs={pageModel.isMfeNbs}
          prefetchGetCheckoutAndSms={pageModel.prefetchGetCheckoutAndSms}
          freePROSetup={pageModel.freePROSetup}
          autoPaySubtext={pageModel.autoPaySubtext}
          autoPayBannerText={pageModel.autoPayBannerText}
          autoPayBannerTooltipText={pageModel.autoPayBannerTooltipText}
          isAccountOwnerEmailEnable={pageModel.isAccountOwnerEmailEnable}
          isPZAIEnable={pageModel.isPZAIEnable}
          isContinueNseEnable={pageModel.isContinueNseEnable}
          isPolymapEnabled={pageModel.isPolymapEnabled}
          isFlowNameEnabled={pageModel.isFlowNameEnabled}
          wifiBackUpBanner={pageModel.wifiBackUpBanner}
          selfSetupBundleId={pageModel.selfSetupBundleId}
        />
        {(pageName === 'progressiveplan' || !(isExpressCheckout() || isExpressCheckoutNSE())) && <PageFooter history={history} />}
        {pageName === 'progressiveplan' && <FooterPlaceholder />}
        {isVoiceBotFeatureEnabled && (
          <VoiceBotProvider featureFlags={voiceBotFeatureFlags}>
            <VoiceBot pageName={pageName} />
          </VoiceBotProvider>
        )}
      </>
    </VZThemeProvider>
  );
};

const RootApp = (pageModel) => (
  <Provider store={store}>
    <BrowserRouter history={history}>
      <ScrollToTop>
        <Routes>
          <Route path="*" element={<InitApp pageModel={pageModel} />} />
        </Routes>
      </ScrollToTop>
    </BrowserRouter>
  </Provider>
);

const modelJsonPath = getModelJsonPath();
ModelManager.initialize({ path: modelJsonPath }).then(render);

